import { useCallback, useMemo } from "react";
import { useMutation } from "@apollo/client/react";
import { gql } from "../__generated__";
import { ClientActivityType } from "../__generated__/graphql";
import type { AssistantReference } from "../types/assistant";

const RECORD_ACTIVITY_MUTATION = gql(`
  mutation RecordActivity(
    $messageId: UUID!
    $activityType: ClientActivityType!
    $surface: String!
    $clientInstanceId: String
    $tab: String
    $sourceKind: String
    $sourceId: String
  ) {
    conversationViewRecordActivity(
      messageId: $messageId
      activityType: $activityType
      surface: $surface
      clientInstanceId: $clientInstanceId
      tab: $tab
      sourceKind: $sourceKind
      sourceId: $sourceId
    )
  }
`);

export interface ActivityRecorder {
  /** The agent switched to `tab` (a code identifier, never a label). */
  tabSwitched: (messageId: string, tab: string) => void;
  /** The agent opened `reference` from the ticket's sources. */
  sourceOpened: (messageId: string, reference: AssistantReference) => void;
}

/** The id of what a reference opens: its document, message, contact or table row. */
function openedSourceId(reference: AssistantReference): string {
  return (
    reference.sourceDocumentId ??
    reference.sourceConversationMessageId ??
    reference.sourceContactContextId ??
    reference.sourceDatabaseTableId ??
    reference.sourceId ??
    reference.id
  );
}

/**
 * Records the panel actions only the client sees, one event per action, every
 * time (the server records the actions it performs itself). Fire-and-forget: a
 * lost event must never disturb the agent.
 */
export function useRecordActivity(surface: string, clientInstanceId?: string): ActivityRecorder {
  const [record] = useMutation(RECORD_ACTIVITY_MUTATION);

  const send = useCallback(
    (variables: {
      messageId: string;
      activityType: ClientActivityType;
      tab?: string;
      sourceKind?: string;
      sourceId?: string;
    }) => {
      record({ variables: { ...variables, surface, clientInstanceId } }).catch(
        (error: unknown) => {
          console.warn("Failed to record activity", error);
        },
      );
    },
    [record, surface, clientInstanceId],
  );

  return useMemo(
    () => ({
      tabSwitched: (messageId, tab) =>
        send({ messageId, activityType: ClientActivityType.TabSwitched, tab }),
      sourceOpened: (messageId, reference) =>
        send({
          messageId,
          activityType: ClientActivityType.SourceOpened,
          sourceKind: reference.type,
          sourceId: openedSourceId(reference),
        }),
    }),
    [send],
  );
}
